<template>
  <div>
    <InputWithValidation
      v-model="adresse.rue"
      rules="required"
      :label="$t(`AdresseView.label-voie`)"
      :placeholder="$t(`AdresseView.placeholder-voie`)"
      :disabled="readonly"
      :group="validationGroup"
      vid="rue"
      @manual-change="updateAddress"
    />

    <InputWithValidation
      v-model="adresse.complement"
      :label="$t(`AdresseView.label-complement`)"
      :placeholder="$t(`AdresseView.placeholder-complement`)"
      :disabled="readonly"
      :group="validationGroup"
      vid="complement"
      @manual-change="updateAddress"
    />

    <InputWithValidation
      v-model="adresse.codePostal"
      :label="$t(`AdresseView.label-codepostal`)"
      :rules="`required|length:5|postalCode|validPostalCode:${suggestedVillesLength}`"
      :placeholder="$t(`AdresseView.placeholder-codepostal`)"
      :disabled="readonly"
      :group="validationGroup"
      :input-notice="$t(`AdresseView.inputnotice-codepostal`)"
      vid="codePostal"
      type="text"
      @manual-change="updateCodePostal"
    />

    <SelectWithValidation
      v-if="suggestedVilles.length !== 0"
      v-model="adresse.ville"
      class="AdresseView-select-ville"
      :group="validationGroup"
      rules="required"
      :disabled="readonly"
      vid="communeSuggested"
      :data="suggestedVilles"
      :select-labels="suggestedVilles"
      :label="$t(`AdresseView.label-commune`)"
      @manual-change="updateAddress"
    />

    <InputWithValidation
      v-if="suggestedVilles.length === 0"
      v-model="adresse.ville"
      rules="required"
      :label="$t(`AdresseView.label-commune`)"
      :placeholder="$t(`AdresseView.placeholder-commune`)"
      :disabled="readonly"
      :group="validationGroup"
      vid="commune"
      @manual-change="updateAddress"
    />

    <InputWithValidation
      v-if="withPays"
      v-model="adresse.pays"
      rules="required"
      :label="$t(`AdresseView.label-pays`)"
      :placeholder="$t(`AdresseView.placeholder-pays`)"
      :disabled="readonly"
      :group="validationGroup"
      vid="pays"
      @manual-change="updateAddress"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import { OrganismeService } from "@/services/rest/organisme/OrganismeService"
import SelectWithValidation from "../inputs/SelectWithValidation.vue"

interface AdresseData {
  rue: string
  complement?: string
  codePostal: string
  ville: string
  pays?: string
  [key: string]: unknown
}

export default defineComponent({
  name: "AdresseView",
  components: {
    InputWithValidation,
    SelectWithValidation,
  },
  props: {
    modelValue: {
      type: Object as PropType<AdresseData>,
      required: true,
    },
    withPays: Boolean,
    readonly: {
      type: Boolean,
      default: false,
    },
    validationGroup: {
      type: String,
      default: "",
    },
  },

  emits: ["change-address", "update:modelValue"],
  data() {
    return {
      organismeService: OrganismeService.INSTANCE,
      suggestedVilles: [] as string[],
      suggestedVillesLength: -1,
    }
  },
  computed: {
    adresse: {
      get(): AdresseData {
        return this.modelValue
      },
      set(value: AdresseData): void {
        this.$emit("update:modelValue", value)
        this.$emit("change-address", value)
      },
    },
  },
  watch: {
    "modelValue.codePostal": {
      handler() {
        this.updateSuggestedVilles()
      },
      immediate: true,
    },
  },
  created(): void {
    this.updateSuggestedVilles()
  },
  methods: {
    updateAddress(): void {
      // Créer une nouvelle instance pour déclencher la réactivité
      const updatedAdresse = { ...this.modelValue }
      this.$emit("update:modelValue", updatedAdresse)
      this.$emit("change-address", updatedAdresse)
    },

    updateCodePostal(): void {
      this.updateAddress()
      this.updateSuggestedVilles()
    },

    async updateSuggestedVilles(): Promise<void> {
      const codePostal = this.modelValue.codePostal as string
      if (!codePostal || codePostal.length !== 5) {
        this.suggestedVilles = []
        this.suggestedVillesLength = -1
        // Mettre à jour la ville via la computed property
        if (this.adresse.ville) {
          const updatedAdresse = { ...this.modelValue, ville: "" }
          this.$emit("update:modelValue", updatedAdresse)
        }
        return
      }
      this.suggestedVilles = await this.organismeService.getVilleOptionsFromCodePostal(codePostal)
      this.suggestedVillesLength = this.suggestedVilles.length

      if (this.suggestedVilles.length === 1) {
        const updatedAdresse = { ...this.modelValue, ville: this.suggestedVilles[0] }
        this.$emit("update:modelValue", updatedAdresse)
      }
    },
  },
})
</script>

<style lang="scss">
.AdresseView-select-ville {
  .select {
    width: 100%;

    select {
      width: 100%;
    }
  }
}
</style>
